import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { VehicleService, CreateVehiclePayload } from '../../services/vehicle.service';
import { CustomerService } from '../../services/customer.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { US_STATES, State } from '../../shared/constants/us-states.constant';
import { VehicleData } from '../../shared/types';
import { getManufacturingYearError } from '../../shared/utils/vehicle-year.util';

export type { VehicleData };

export interface VehicleFormData {
  state: string;
  licensePlate: string;
}

/**
 * Form component for fetching vehicle details by license plate/state and for manually creating or editing vehicle records; includes optional booking selection and UI state management.
 * @component
 * @selector app-add-vehicle-form
 * @example
 *   <app-add-vehicle-form
 *     [vehicleForm]="vehicleForm"
 *     [customerId]="customerId"
 *     [isEditMode]="false"
 *     (vehicleDataChange)="onVehicleDataChange($event)"
 *     (vehicleCreatedManually)="onVehicleCreated($event)">
 *   </app-add-vehicle-form>
 * @input {VehicleFormData} vehicleForm - Current form values for state and licensePlate; emitted back via vehicleFormChange on update.
 * @input {VehicleData | null} vehicleData - Fetched or current vehicle details; may be updated by the component (vehicleDataChange).
 * @input {string} vehicleErrorMessage - Error message shown when fetch/create fails; used to trigger manual entry visibility.
 * @input {boolean} isLoadingVehicle - Loading flag for fetch/create operations.
 * @input {boolean} isBookingChecked - Initial checked state for booking checkbox.
 * @input {boolean} isBookingDisabled - Whether booking checkbox should be disabled.
 * @input {boolean} isRadioButton - Whether radio button UI is used for vehicle selection.
 * @input {string} headerText - Optional header text displayed above the form (default: 'Add Vehicle').
 * @input {boolean} showHeader - Whether to render the header.
 * @input {boolean} showSaveCancelButtons - Whether to show save/cancel controls (used in edit flows).
 * @input {boolean} showBookingCheckbox - Whether to display booking checkbox when multiple vehicles available.
 * @input {string} customerId - Customer ID required when creating a vehicle manually via POST.
 * @input {boolean} isEditMode - When true, manual make/model/year update only affects local data (parent performs PUT on save); when false, creating manually triggers POST.
 * @output {EventEmitter<VehicleFormData>} vehicleFormChange - Emitted when the form fields change.
 * @output {EventEmitter<VehicleData | null>} vehicleDataChange - Emitted when vehicle details are fetched, created, or manually updated.
 * @output {EventEmitter<string>} vehicleErrorMessageChange - Emitted to surface error messages to the parent.
 * @output {EventEmitter<boolean>} isLoadingVehicleChange - Emitted to indicate loading state changes for fetch/create operations.
 * @output {EventEmitter<boolean>} bookingChange - Emitted when booking checkbox is toggled by the user.
 * @output {EventEmitter<VehicleData>} vehicleCreatedManually - Emitted after a successful manual creation POST (includes created vehicle data and id).
 * @output {EventEmitter<boolean>} manualFieldsVisibilityChange - Emitted when the component shows or hides manual entry fields (e.g., when vehicle not found).
 *
 * Key methods:
 * - onFormChange(): emits vehicleFormChange when the license plate/state inputs change.
 * - onFetchVehicleDetails(): requests vehicle data from the service if canFetchVehicle and not currently loading; updates vehicleData, error message, and manual field visibility. Handles API success and error responses.
 * - onCreateVehicleManually(): validates manual fields and either updates local vehicleData in edit mode or creates a new vehicle via service POST in add mode; emits vehicleCreatedManually on success.
 * - validateManualFields(): validates make, model, and year (exactly four digits, range 1900..currentYear+1) and sets per-field error messages.
 * - onMakeInput(), onModelInput(), onYearInput(), onYearKeydown(), onYearPaste(): year field digits-only (block keys, sanitize paste, normalize on input); clear errors when editing manual fields.
 * - onBookingChange(event): emits bookingChange with the checkbox state.
 * - isVehicleNotFoundError(message): (private) checks if an error string indicates a "not found" or invalid-plate condition to decide whether to show manual entry.
 *
 * Lifecycle hooks:
 * - ngOnChanges(changes: SimpleChanges): watches vehicleErrorMessage and toggles manualFieldsVisibility when a "vehicle not found" style message appears.
 */
@Component({
  selector: 'app-add-vehicle-form',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSelectModule],
  templateUrl: './add-vehicle-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-vehicle-form.component.scss',
})
export class AddVehicleFormComponent implements OnChanges {
  @Input() vehicleForm: VehicleFormData = { state: '', licensePlate: '' };
  @Input() vehicleData: VehicleData | null = null;
  @Input() vehicleErrorMessage = '';
  @Input() isLoadingVehicle = false;
  @Input() isBookingChecked = true;
  @Input() isBookingDisabled = false;
  @Input() isRadioButton = false;
  @Input() headerText = 'Add Vehicle';
  @Input() showHeader = true;
  @Input() showSaveCancelButtons = false;
  @Input() showBookingCheckbox = true; // Show booking checkbox only when there are multiple vehicles
  @Input() customerId = ''; // Customer ID for creating vehicle manually
  /** When true, manual make/model only updates local data; parent will use PUT on Save. When false, manual entry creates vehicle via POST. */
  @Input() isEditMode = false;

  @Output() vehicleFormChange = new EventEmitter<VehicleFormData>();
  @Output() vehicleDataChange = new EventEmitter<VehicleData | null>();
  @Output() vehicleErrorMessageChange = new EventEmitter<string>();
  @Output() isLoadingVehicleChange = new EventEmitter<boolean>();
  @Output() bookingChange = new EventEmitter<boolean>();
  @Output() vehicleCreatedManually = new EventEmitter<VehicleData>(); // Emit when vehicle is created manually
  @Output() manualFieldsVisibilityChange = new EventEmitter<boolean>(); // Emit when manual fields visibility changes
  @Output() customerDataChange = new EventEmitter<any>(); // Emit when customer is found by plate

  private vehicleService = inject(VehicleService);
  private customerService = inject(CustomerService);
  private messageService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  // Manual vehicle entry fields
  manualMake = '';
  manualModel = '';
  manualYear = '';
  showManualFields = false;
  isCreatingVehicle = false;
  manualMakeError = '';
  manualModelError = '';
  manualYearError = '';

  states: State[] = US_STATES;

  get canFetchVehicle(): boolean {
    return Boolean(this.vehicleForm.state && this.vehicleForm.licensePlate?.trim());
  }

  /** Form fields complete; button stays enabled so users see an error if customer is missing. */
  get isManualVehicleFormReady(): boolean {
    return Boolean(
      this.manualMake?.trim() &&
      this.manualModel?.trim() &&
      !this.getManufacturingYearError(this.manualYear) &&
      this.vehicleForm.state &&
      this.vehicleForm.licensePlate?.trim() &&
      !this.isCreatingVehicle
    );
  }

  /** @deprecated Use isManualVehicleFormReady; kept for any external references. */
  get canCreateVehicleManually(): boolean {
    return this.isManualVehicleFormReady && !!this.customerId;
  }

  get showVehicleFields(): boolean {
    return this.showManualFields;
  }

  onFormChange(): void {
    this.vehicleFormChange.emit(this.vehicleForm);
  }

  private isVehicleNotFoundError(message: string): boolean {
    const lower = message.toLowerCase();
    return (
      lower.includes('vehicle not found') ||
      lower.includes('invalid license plate') ||
      lower.includes('license plate not found') ||
      lower.includes('no vehicle data available') ||
      // svc-customer wording for a lookup miss / unconfigured AskOtto: the
      // operator can only proceed by typing the vehicle in.
      lower.includes('no vehicle details found') ||
      lower.includes('lookup is not configured')
    );
  }

  /**
   * Fetch vehicle details using the license plate and state from the form, emit loading and result events, and handle errors including toggling manual field visibility.
   * @example
   * nFetchVehicleDetails()
   * void
   * @returns {{void}} No return value; triggers events, updates component state, and displays error messages.
   **/
  onFetchVehicleDetails(): void {
    if (!this.canFetchVehicle || this.isLoadingVehicle) {
      return;
    }

    this.setVehicleLoading(true);
    this.vehicleErrorMessageChange.emit('');
    this.vehicleDataChange.emit(null);

    const payload = {
      license_plate: this.vehicleForm.licensePlate.trim(),
      state: this.vehicleForm.state,
      // Read-only: Save writes the vehicle, once, with the customer on it (CWS-1398).
      persist: false,
    };

    this.vehicleService.getVehicleData(payload).subscribe({
      next: response => {
        this.setVehicleLoading(false);
        if (response.status && response.data) {
          this.vehicleDataChange.emit(response.data);
          this.vehicleErrorMessageChange.emit('');
          this.cdr.markForCheck();

          // Also fetch customer info by license plate to auto-populate step 2 data if missing
          this.customerService.getCustomerByLicensePlate(payload.license_plate).subscribe({
            next: custRes => {
              if (custRes.status && custRes.data) {
                this.customerDataChange.emit(custRes.data);
              }
            },
            error: () => {}, // Silent fail for customer lookup
          });
        } else if (response.status && !response.data) {
          const emptyMessage = 'No vehicle data available';
          this.vehicleErrorMessageChange.emit(emptyMessage);
          this.showManualFields = true;
          this.manualFieldsVisibilityChange.emit(true);
          this.messageService.add({
            severity: 'info',
            summary: 'No data available',
            detail: emptyMessage,
          });
          this.cdr.markForCheck();
        } else {
          const errorMessage = response.message || 'Failed to fetch vehicle data';
          this.vehicleErrorMessageChange.emit(errorMessage);
          const showManual = this.isVehicleNotFoundError(errorMessage);
          this.showManualFields = showManual;
          this.manualFieldsVisibilityChange.emit(showManual);
          this.cdr.markForCheck();
        }
      },
      error: error => {
        this.setVehicleLoading(false);
        const errorMessage =
          error.error?.message || 'Failed to fetch vehicle data. Please try again.';
        this.vehicleErrorMessageChange.emit(errorMessage);
        this.vehicleDataChange.emit(null);
        const showManual = this.isVehicleNotFoundError(errorMessage);
        this.showManualFields = showManual;
        this.manualFieldsVisibilityChange.emit(showManual);

        this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        this.cdr.markForCheck();
      },
    });
  }

  /** Keep local loading in sync so the fetch-button spinner paints without waiting on parent CD. */
  private setVehicleLoading(isLoading: boolean): void {
    this.isLoadingVehicle = isLoading;
    this.isLoadingVehicleChange.emit(isLoading);
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vehicleErrorMessage'] && this.vehicleErrorMessage) {
      const showManual = this.isVehicleNotFoundError(this.vehicleErrorMessage);
      this.showManualFields = showManual;
      this.manualFieldsVisibilityChange.emit(showManual);
    }
  }

  onMakeInput(): void {
    this.manualMakeError = '';
    // Clear vehicle error when user starts typing
    if (this.vehicleErrorMessage) {
      this.vehicleErrorMessageChange.emit('');
    }
  }

  onModelInput(): void {
    this.manualModelError = '';
    // Clear vehicle error when user starts typing
    if (this.vehicleErrorMessage) {
      this.vehicleErrorMessageChange.emit('');
    }
  }

  /**
   * Block non-digit keys before they appear in the field (complements paste + input sanitization).
   */
  onYearKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const navKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);
    if (navKeys.has(event.key)) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      return;
    }

    if (event.key.length === 1 && /^[0-9]$/.test(event.key)) {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const nextLen = this.manualYear.length - (end - start) + 1;
      if (nextLen > 4) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
  }

  /** Insert only digits from clipboard, max 4 total. */
  onYearPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const raw = event.clipboardData?.getData('text') ?? '';
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const cur = this.manualYear ?? '';
    const merged = (cur.slice(0, start) + digits + cur.slice(end)).replace(/\D/g, '').slice(0, 4);
    this.manualYear = merged;
    this.manualYearError = '';
    if (this.vehicleErrorMessage) {
      this.vehicleErrorMessageChange.emit('');
    }
    requestAnimationFrame(() => {
      const pos = merged.length;
      input.setSelectionRange(pos, pos);
    });
  }

  /**
   * Normalize year to digits-only (defense in depth for IME / programmatic updates).
   */
  onYearInput(): void {
    this.manualYearError = '';
    if (this.manualYear) {
      const numericOnly = this.manualYear.toString().replace(/\D/g, '').slice(0, 4);
      if (numericOnly !== this.manualYear) {
        this.manualYear = numericOnly;
      }
    }
    if (this.vehicleErrorMessage) {
      this.vehicleErrorMessageChange.emit('');
    }
  }

  /**
   * Validate manual vehicle input fields (make, model, year) and set component error messages for any invalid field.
   * @example
   * validateManualFields()
   * true
   * @returns {boolean} True if all manual fields are valid, otherwise false.
   **/
  /**
   * Returns an error message if the value is not a valid 4-digit manufacturing year, or null if valid.
   */
  private getManufacturingYearError(raw: string | undefined): string | null {
    return getManufacturingYearError(raw);
  }

  /**
   * Validates the manual vehicle fields and sets error messages when required fields are missing or invalid.
   * @example
   * validateManualFields()
   * true
   * @returns {boolean} Returns true when all manual fields are valid; otherwise returns false.
   */
  validateManualFields(): boolean {
    let isValid = true;
    this.manualMakeError = '';
    this.manualModelError = '';
    this.manualYearError = '';

    if (!this.manualMake?.trim()) {
      this.manualMakeError = 'Make is required';
      isValid = false;
    }

    if (!this.manualModel?.trim()) {
      this.manualModelError = 'Model is required';
      isValid = false;
    }

    const yearErr = this.getManufacturingYearError(this.manualYear);
    if (yearErr) {
      this.manualYearError = yearErr;
      isValid = false;
    }

    return isValid;
  }

  /**
   * Validates manual vehicle fields and either updates local vehicle data in edit mode or creates a new vehicle via the API in add mode, emitting events and showing user messages accordingly.
   * @example
   * nCreateVehicleManually()
   * undefined
   * @returns {void} Executes the manual vehicle create/update workflow and side-effects (emits events, updates loading state, and displays messages); does not return a value.
   **/
  onCreateVehicleManually(): void {
    if (!this.validateManualFields()) {
      return;
    }

    // Edit mode: only update local vehicle data; parent will call PUT on Save. Do not call POST.
    if (this.isEditMode) {
      const vehicleData: VehicleData = {
        status: 'active',
        vin: this.vehicleData?.vin ?? '',
        year: parseInt(this.manualYear, 10),
        make: this.manualMake.trim(),
        model: this.manualModel.trim(),
        state: this.vehicleForm.state,
        plate: this.vehicleForm.licensePlate.trim(),
        _utc: '',
        last_updated: '',
        cached: '',
      };
      this.vehicleDataChange.emit(vehicleData);
      this.vehicleErrorMessageChange.emit('');
      this.showManualFields = false;
      this.manualFieldsVisibilityChange.emit(false);
      this.messageService.add({
        severity: 'success',
        summary: 'Details updated',
        detail: 'Click Save to update the vehicle.',
      });
      return;
    }

    // Add mode: create new vehicle via POST
    if (!this.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID is required to create vehicle',
      });
      return;
    }

    this.isCreatingVehicle = true;
    this.isLoadingVehicleChange.emit(true);

    const payload: CreateVehiclePayload = {
      license_plate: this.vehicleForm.licensePlate.trim(),
      state: this.vehicleForm.state,
      vin: '', // VIN not available when creating manually
      make: this.manualMake.trim(),
      model: this.manualModel.trim(),
      year: parseInt(this.manualYear, 10),
      customer_id: this.customerId,
      is_family_plan: false,
    };

    this.vehicleService.createVehicle(payload).subscribe({
      next: response => {
        this.isCreatingVehicle = false;
        this.isLoadingVehicleChange.emit(false);

        if (response.status && response.data) {
          // Create VehicleData object from response
          const vehicleData: VehicleData = {
            status: response.data.status || 'active',
            vin: response.data.vin || '',
            year: response.data.year || parseInt(this.manualYear, 10),
            make: response.data.make || this.manualMake.trim(),
            model: response.data.model || this.manualModel.trim(),
            state: response.data.state || this.vehicleForm.state,
            plate: response.data.license_plate || this.vehicleForm.licensePlate.trim(),
            _utc: response.data._utc || '',
            last_updated: response.data.last_updated || '',
            cached: response.data.cached || '',
          };

          // Add ID to vehicleData for parent component use
          const vehicleDataWithId: any = {
            ...vehicleData,
            id: response.data.id || '',
          };

          this.vehicleDataChange.emit(vehicleData);
          this.vehicleErrorMessageChange.emit('');
          this.showManualFields = false;
          this.manualFieldsVisibilityChange.emit(false); // Hide manual fields after successful creation

          // Emit event to notify parent that vehicle was created manually (include ID)
          this.vehicleCreatedManually.emit(vehicleDataWithId);

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vehicle created successfully',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to create vehicle',
          });
        }
      },
      error: error => {
        this.isCreatingVehicle = false;
        this.isLoadingVehicleChange.emit(false);
        const errorMessage = error.error?.message || 'Failed to create vehicle. Please try again.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
      },
    });
  }

  onBookingChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.bookingChange.emit(target.checked);
  }
}
